/**
 * Dynamic Box Widget
 * Uses window.skioDynamicBox and window.bundleProducts
 */

class DynamicBoxWidget {
  constructor() {
    // Validate required data
    if (!window.skioDynamicBox || !window.bundleProducts) {
      // console.error('DynamicBoxWidget: Required window objects not found');
      return;
    }

    // Store references to global data
    this.skioDynamicBox = window.skioDynamicBox;
    this.bundleProducts = window.bundleProducts;
    this.box_config = this.skioDynamicBox.box_config;
    this.currency = window.shopCurrency || { symbol: 'kr', code: 'SEK' };
    
    // State management
    this.selectedOption = 'subscription'; // 'subscription' or 'onetime'
    this.selectedSellingPlan = null;
    this.productQuantities = {}; // { productId: quantity }
    this.onetimeProducts = window.onetimeBoxProducts || [];
    this.onetimeProductQuantities = {}; // { productId: quantity } - always added without selling_plan
    this.unitText = this.bundleProducts[0]?.antal_enhet || 'måltider'; // Dynamic unit text from first product
    
    // console.log('📦 DynamicBoxWidget initialized', {
    //   products: this.bundleProducts.length,
    //   sellingPlans: this.box_config.sellingPlanGroup?.length || 0,
    //   currency: this.currency,
    //   unitText: this.unitText
    // });
    
    // Initialize
    this.init();
  }

  /**
   * Format price with proper decimal handling
   * Show 1 decimal unless it ends in .0, then show integer
   * @param {number} amount - Amount in cents
   * @returns {string} Formatted price with currency
   */
  formatPrice(amount) {
    const priceInCurrency = amount / 100;
    const rounded = Math.round(priceInCurrency * 10) / 10; // Round to 1 decimal
    const hasDecimals = (rounded % 1) !== 0;
    const formatted = hasDecimals 
      ? rounded.toFixed(1) 
      : Math.round(rounded);
    
    return `${formatted} ${this.currency.symbol}`;
  }

  slugifyReadable(value) {
    return String(value || '')
      .replace(/[åÅ]/g, 'a')
      .replace(/[äÄ]/g, 'a')
      .replace(/[öÖ]/g, 'o')
      .replace(/[ëË]/g, 'e')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '') || 'na';
  }

  applyCustomBoxReadableIds() {
    const counts = {};
    const nextId = (base) => {
      counts[base] = (counts[base] || 0) + 1;
      return counts[base] > 1 ? `${base}-${counts[base]}` : base;
    };
    const contentToken = (el, fallback) => {
      const raw = (el?.getAttribute('aria-label') || el?.textContent || '').trim();
      const token = this.slugifyReadable(raw);
      return token !== 'na' ? token : fallback;
    };

    const productItems = document.querySelectorAll('.product-item');
    productItems.forEach((item) => {
      const productName = this.slugifyReadable(
        item.dataset.productTitle ||
        item.querySelector('.product-name')?.textContent ||
        item.dataset.productId ||
        ''
      );
      if (productName === 'na') return;

      item.querySelectorAll('.quantity-btn.increase').forEach((btn) => {
        btn.id = nextId(`product-custom-box-btn-add-${productName}`);
      });
      item.querySelectorAll('.quantity-btn.decrease').forEach((btn) => {
        btn.id = nextId(`product-custom-box-btn-decrease-${productName}`);
      });
      item.querySelectorAll('.quantity-input').forEach((input) => {
        input.id = nextId(`product-custom-box-input-quantity-${productName}`);
      });
    });

    document.querySelectorAll('.js-add-to-cart, #addToCart').forEach((btn) => {
      btn.id = nextId('product-custom-box-btn-add-to-cart');
    });

    // Keep frequency controls untouched (editor + app hooks rely on stable #frequency selectors),
    // but still tag subscription/onetime toggles for click tracking.
    document.querySelectorAll('.subscription-box').forEach((el) => {
      el.id = nextId('product-custom-box-div-subscription');
    });
    document.querySelectorAll('.onetime-box').forEach((el) => {
      el.id = nextId('product-custom-box-div-onetime');
    });
  }

  /**
   * Initialize the widget
   */
  init() {
    // Ensure onetime products reference is current
    if (!this.onetimeProducts.length && window.onetimeBoxProducts && window.onetimeBoxProducts.length) {
      this.onetimeProducts = window.onetimeBoxProducts;
    }

    // Populate frequency dropdown
    this.populateFrequencyOptions();
    
    // Set up event listeners
    this.setupEventListeners();
    
    // Initialize quantities
    this.initializeProductQuantities();
    
    // Update unit text displays
    this.updateUnitTextDisplays();
    
    // Set initial state based on whether subscription box exists in DOM
    const hasSubscriptionBox = !!document.querySelector('.subscription-box');
    this.selectOption(hasSubscriptionBox ? 'subscription' : 'onetime');
    
    // Setup intersection observer for fixed cart button
    this.setupIntersectionObserver();

    // Adjust information icon visibility based on title wrapping on mobile
    this.updateInfoIconVisibility();
    this.applyCustomBoxReadableIds();
    
    // console.log('✅ DynamicBoxWidget ready');
  }

  /**
   * Populate frequency dropdown from boxconfig.sellingPlanGroup
   */
  populateFrequencyOptions() {
    const frequencySelect = document.getElementById('frequency');
    if (!frequencySelect) {
      // console.warn('Frequency select not found');
      return;
    }

    const sellingPlanGroup = this.box_config.sellingPlanGroup;
    if (!sellingPlanGroup || sellingPlanGroup.length === 0) {
      frequencySelect.innerHTML = '<option value="">No plans available</option>';
      return;
    }

    // Clear existing options
    frequencySelect.innerHTML = '';

    // Sort by position
    const sortedPlans = [...sellingPlanGroup].sort((a, b) => a.position - b.position);

    // Get translated frequency text from Shopify translations (set in Liquid)
    const frequencyText = window.boxTranslations?.frequency || 'veckor'; // "veckor", "weeks", "Wochen", etc.
    const monthText = window.boxTranslations?.monthFrequency || 'månader';

    // Create options
    sortedPlans.forEach(plan => {
      const option = document.createElement('option');
      option.value = plan.gid;
      
      // Format: "{intervalCount} {frequencyText}"
      const interval = plan.interval.toLowerCase();
      const intervalCount = plan.intervalCount;
      
      if (interval === 'week') {
        option.textContent = `${intervalCount} ${frequencyText}`;
      } else if (interval === 'month') {
        option.textContent = `${intervalCount} ${monthText}`;
      } else {
        option.textContent = plan.title || `${intervalCount} ${interval}`;
      }
      
      frequencySelect.appendChild(option);
    });

    // Select first option by default
    if (sortedPlans.length > 0) {
      frequencySelect.value = sortedPlans[0].gid;
      this.selectedSellingPlan = sortedPlans[0];
    }

    // console.log(`📅 Populated ${sortedPlans.length} frequency options`);
  }

  /**
   * Set up event listeners
   */
  setupEventListeners() {
    // Subscription/Onetime selection
    const subscriptionBox = document.querySelector('.subscription-box');
    const onetimeBox = document.querySelector('.onetime-box');

    if (subscriptionBox) {
      subscriptionBox.addEventListener('click', () => this.selectOption('subscription'));
    }

    if (onetimeBox) {
      onetimeBox.addEventListener('click', () => this.selectOption('onetime'));
    }

    // Frequency dropdown
    const frequencySelect = document.getElementById('frequency');
    if (frequencySelect) {
      frequencySelect.addEventListener('change', (e) => {
        const selectedGid = e.target.value;
        this.selectedSellingPlan = this.box_config.sellingPlanGroup.find(plan => plan.gid === selectedGid);
        // console.log('Selected selling plan:', this.selectedSellingPlan);
      });
    }

    // Quantity controls
    this.setupQuantityControls();

    // Add to cart button
    const addToCartButtons = document.querySelectorAll('.js-add-to-cart, #addToCart');
    addToCartButtons.forEach(button => {
      button.addEventListener('click', () => this.handleAddToCart());
    });

    // Recalculate info icon visibility on viewport changes (primarily affects mobile)
    window.addEventListener('resize', () => {
      if (typeof this.updateInfoIconVisibility === 'function') {
        this.updateInfoIconVisibility();
      }
    });
  }

  /**
   * Initialize product quantities from DOM
   */
  initializeProductQuantities() {
    const productItems = document.querySelectorAll('.product-container .product-item');
    productItems.forEach(item => {
      const productId = item.dataset.productId;
      const quantityInput = item.querySelector('.quantity-input');
      if (quantityInput) {
        this.productQuantities[productId] = parseInt(quantityInput.value) || 0;
      }
    });

    const onetimeItems = document.querySelectorAll('.onetime-product-item');
    onetimeItems.forEach(item => {
      const productId = item.dataset.productId;
      const quantityInput = item.querySelector('.quantity-input');
      if (quantityInput) {
        this.onetimeProductQuantities[productId] = parseInt(quantityInput.value) || 0;
      }
    });
  }

  /**
   * Set up quantity control buttons
   */
  setupQuantityControls() {
    // Setup controls for subscription box products
    const productItems = document.querySelectorAll('.product-container .product-item');
    this._setupQuantityControlsForItems(productItems, this.productQuantities, this.bundleProducts, true);

    // Setup controls for onetime-only products
    const onetimeItems = document.querySelectorAll('.onetime-product-item');
    this._setupQuantityControlsForItems(onetimeItems, this.onetimeProductQuantities, this.onetimeProducts, false);
  }

  _setupQuantityControlsForItems(items, quantityMap, productDataList, affectsVolume) {
    items.forEach(item => {
      const productId = item.dataset.productId;
      const increaseBtn = item.querySelector('.quantity-btn.increase');
      const decreaseBtn = item.querySelector('.quantity-btn.decrease');
      const quantityInput = item.querySelector('.quantity-input');

      if (!increaseBtn || !decreaseBtn || !quantityInput) return;

      const productData = productDataList.find(p => {
        const baseId = p.product.id.replace('gid://shopify/Product/', '');
        return baseId === productId;
      });
      const rawStock = productData?.in_stock;
      const isInStock = !(rawStock === false || rawStock === 'false' || rawStock === 0 || rawStock === '0');
      if (!isInStock) {
        const backInStock = productData?.back_in_stock_text;
        const outText = productData?.out_of_stock_text || 'In stock';
        const container = item.querySelector('.quantity-selector') || quantityInput.parentElement;
        if (container) {
          container.innerHTML = '';
          const tag = document.createElement('span');
          tag.textContent = backInStock ? `${outText} ${backInStock}` : outText;
          tag.style.display = 'inline-flex';
          tag.style.alignItems = 'center';
          tag.style.justifyContent = 'center';
          tag.style.padding = '4px 10px';
          tag.style.borderRadius = '999px';
          tag.style.backgroundColor = '#b84040';
          tag.style.color = '#ffffff';
          tag.style.fontSize = '12px';
          tag.style.fontWeight = '600';
          container.appendChild(tag);
        }
        quantityMap[productId] = 0;
        return;
      }

      if (parseInt(quantityInput.value) === 0) {
        quantityInput.classList.add('hidden');
        decreaseBtn.classList.add('hidden');
      }

      increaseBtn.addEventListener('click', () => {
        const currentValue = parseInt(quantityInput.value) || 0;
        const newValue = currentValue + 1;
        quantityInput.value = newValue;
        quantityMap[productId] = newValue;

        if (currentValue === 0) {
          quantityInput.classList.remove('hidden');
          decreaseBtn.classList.remove('hidden');
        }

        decreaseBtn.disabled = false;
        if (affectsVolume) {
          this.updatePrices();
        } else {
          const volMult = 1 - (this.getVolumeDiscount(this.getTotalQuantity()) / 100);
          this.updateBoxTotals(null, volMult);
        }
        this.updateVolumeDiscountProgress();
        this._autoSwitchOption();
        this.updateAddToCartButton();
        this.applyCustomBoxReadableIds();

        if (typeof this.updateInfoIconVisibility === 'function') {
          this.updateInfoIconVisibility();
        }
      });

      decreaseBtn.addEventListener('click', () => {
        const currentValue = parseInt(quantityInput.value) || 0;
        if (currentValue > 0) {
          const newValue = currentValue - 1;
          quantityInput.value = newValue;
          quantityMap[productId] = newValue;

          if (newValue === 0) {
            quantityInput.classList.add('hidden');
            decreaseBtn.classList.add('hidden');
          }

          decreaseBtn.disabled = newValue === 0;
          if (affectsVolume) {
            this.updatePrices();
          } else {
            const volMult = 1 - (this.getVolumeDiscount(this.getTotalQuantity()) / 100);
            this.updateBoxTotals(null, volMult);
          }
          this.updateVolumeDiscountProgress();
          this._autoSwitchOption();
          this.updateAddToCartButton();
          this.applyCustomBoxReadableIds();

          if (typeof this.updateInfoIconVisibility === 'function') {
            this.updateInfoIconVisibility();
          }
        }
      });
    });
  }

  /**
   * Auto-switch between subscription and onetime based on what's selected.
   * If only onetime products → switch to onetime.
   * If any subscription product is added → switch back to subscription.
   */
  _autoSwitchOption() {
    const hasSubscriptionBox = !!document.querySelector('.subscription-box');
    const subQty = this.getSubscriptionQuantity();
    const otQty = this.getOnetimeTotalQuantity();

    if (subQty === 0 && otQty > 0 && this.selectedOption === 'subscription') {
      this.selectOption('onetime');
    } else if (subQty > 0 && this.selectedOption === 'onetime' && hasSubscriptionBox) {
      this.selectOption('subscription');
    }
  }

  /**
   * Hide the information icon on mobile only when the product title area wraps
   * to a new line (i.e. when there isn't enough horizontal space).
   */
  updateInfoIconVisibility() {
    const isMobile = window.innerWidth <= 768;
    const icons = document.querySelectorAll('.information-icon');

    if (!isMobile) {
      // Always show icons on larger screens
      icons.forEach(icon => icon.classList.remove('information-icon--hidden'));
      return;
    }

    const productItems = document.querySelectorAll('.product-item');
    productItems.forEach(item => {
      const nameWrapper = item.querySelector('.product-name-wrapper');
      const nameEl = item.querySelector('.product-name');
      const icon = item.querySelector('.information-icon');
      if (!nameWrapper || !nameEl || !icon) return;

      // Ensure icon is visible while we measure for overflow
      icon.classList.remove('information-icon--hidden');

      // Detect if the title text has wrapped to more than one line
      // by comparing its rendered height to its line-height
      const computed = window.getComputedStyle(nameEl);
      const lineHeight = parseFloat(computed.lineHeight);
      const height = nameEl.getBoundingClientRect().height;
      const isWrapped =
        !isNaN(lineHeight) && lineHeight > 0
          ? height > lineHeight * 1.3
          : nameEl.scrollHeight > nameEl.clientHeight + 1;

      if (isWrapped) {
        icon.classList.add('information-icon--hidden');
      } else {
        icon.classList.remove('information-icon--hidden');
      }
    });
  }

  /**
   * Select subscription or onetime option
   * @param {string} option - 'subscription' or 'onetime'
   */
  selectOption(option) {
    this.selectedOption = option;
    
    const subscriptionBox = document.querySelector('.subscription-box');
    const onetimeBox = document.querySelector('.onetime-box');
    const frequencyContainer = document.querySelector('.subscription-frequency-container-wrapper');
    const bestValueBar = document.querySelector('.best-value-bar');
    const productContainer = document.querySelector('.product-container');
    const productPrices = document.querySelectorAll('.product-container .product-price');

    if (option === 'subscription') {
      subscriptionBox?.classList.add('selected');
      onetimeBox?.classList.remove('selected');
      frequencyContainer?.style.setProperty('display', 'flex');
      bestValueBar?.classList.remove('hidden');
      
      // Highlight product prices when subscription is selected
      if (productContainer) {
        productContainer.classList.add('subscription-selected');
      }
      productPrices.forEach(priceEl => {
        priceEl.style.color = '#387A62';
        priceEl.style.fontWeight = '600';
      });
    } else {
      onetimeBox?.classList.add('selected');
      subscriptionBox?.classList.remove('selected');
      frequencyContainer?.style.setProperty('display', 'none');
      bestValueBar?.classList.add('hidden');
      
      // Reset product prices when onetime is selected
      if (productContainer) {
        productContainer.classList.remove('subscription-selected');
      }
      productPrices.forEach(priceEl => {
        priceEl.style.color = '#6b7280';
        priceEl.style.fontWeight = '500';
      });
    }

    // Update individual product prices based on selection
    this.updateProductPrices();
    
    // Always update box totals (subscription price always shows subscription discount)
    const volumeMultiplier = 1 - (this.getVolumeDiscount(this.getTotalQuantity()) / 100);
    this.updateBoxTotals(null, volumeMultiplier);
    
    this.updateAddToCartButton();

    // console.log(`🔄 Selected option: ${option}`);
  }

  /**
   * Update individual product prices based on subscription/onetime selection
   * Uses boxconfig.sellingPlanGroup[0].priceAdjustmentAmount and priceAdjustmentType
   */
  updateProductPrices() {
    const isSubscription = this.selectedOption === 'subscription';
    const totalQuantity = this.getTotalQuantity();
    
    // Get subscription discount from first selling plan (they all have same discount)
    const sellingPlan = this.box_config.sellingPlanGroup?.[0];
    const priceAdjustmentType = sellingPlan?.priceAdjustmentType || 'PERCENTAGE';
    const priceAdjustmentAmount = sellingPlan?.priceAdjustmentAmount || 20;
    
    // Calculate subscription multiplier
    let subscriptionMultiplier = 1.0;
    if (isSubscription && priceAdjustmentType === 'PERCENTAGE') {
      // If adjustment is 20%, customer pays 80% (1 - 0.20 = 0.80)
      subscriptionMultiplier = 1 - (priceAdjustmentAmount / 100);
    }

    // Get volume discount from percentDiscountTier
    const volumeDiscountPercent = this.getVolumeDiscount(totalQuantity);
    const volumeMultiplier = 1 - (volumeDiscountPercent / 100);

    // console.log('💰 Price calculation:', {
    //   isSubscription,
    //   totalQuantity,
    //   subscriptionMultiplier,
    //   volumeMultiplier,
    //   volumeDiscountPercent
    // });

    // Update each subscription product's displayed price (exclude onetime items)
    const productItems = document.querySelectorAll('.product-container .product-item');
    productItems.forEach(item => {
      const productId = item.dataset.productId;
      const priceElement = item.querySelector('.product-price');
      
      if (!priceElement) return;
      
      // Find product data
      const productData = this.bundleProducts.find(p => {
        const pId = p.product.id.replace('gid://shopify/Product/', '');
        return pId === productId;
      });
      
      if (!productData) return;
      
      // Calculate price
      const basePrice = productData.product.price_min;
      const finalPrice = basePrice * subscriptionMultiplier * volumeMultiplier;
      
      // Format and display using formatPrice function
      priceElement.textContent = this.formatPrice(finalPrice);
    });
  }

  /**
   * Update all product prices based on subscription/onetime selection
   * Uses boxconfig.sellingPlanGroup[0].priceAdjustmentAmount and priceAdjustmentType
   */
  updatePrices() {
    // Update individual product prices
    this.updateProductPrices();
    
    // Always update box totals (subscription price always shows subscription discount)
    const volumeMultiplier = 1 - (this.getVolumeDiscount(this.getTotalQuantity()) / 100);
    this.updateBoxTotals(null, volumeMultiplier);
  }

  /**
   * Get volume discount percentage based on quantity
   * @param {number} quantity - Total quantity
   * @returns {number} Discount percentage
   */
  getVolumeDiscount(quantity) {
    const percentDiscountTier = this.box_config.percentDiscountTier || {};
    let highestDiscount = 0;
    
    Object.keys(percentDiscountTier).forEach(tier => {
      const tierQuantity = parseInt(tier);
      if (quantity >= tierQuantity) {
        const discountPercent = parseFloat(percentDiscountTier[tier]);
        if (discountPercent > highestDiscount) {
          highestDiscount = discountPercent;
        }
      }
    });
    
    return highestDiscount;
  }

  /**
   * Update subscription and onetime box totals
   */
  updateBoxTotals(subscriptionMultiplier, volumeMultiplier) {
    const subQuantity = this.getTotalQuantity();
    
    // Calculate total meals from subscription products
    let totalMeals = 0;
    this.bundleProducts.forEach(product => {
      const productId = product.product.id.replace('gid://shopify/Product/', '');
      const quantity = this.productQuantities[productId] || 0;
      const antalPerProduct = product.antal_i || 14;
      totalMeals += quantity * antalPerProduct;
    });

    // Also count onetime product meals
    const onetimeProdsForMeals = this.onetimeProducts.length ? this.onetimeProducts : (window.onetimeBoxProducts || []);
    onetimeProdsForMeals.forEach(product => {
      const productId = product.product.id.replace('gid://shopify/Product/', '');
      const quantity = this.onetimeProductQuantities[productId] || 0;
      const antalPerProduct = product.antal_i || 14;
      totalMeals += quantity * antalPerProduct;
    });
    
    // Always calculate subscription multiplier with discount for subscription price display
    // regardless of current selection
    const sellingPlan = this.box_config.sellingPlanGroup?.[0];
    const priceAdjustmentType = sellingPlan?.priceAdjustmentType || 'PERCENTAGE';
    const priceAdjustmentAmount = sellingPlan?.priceAdjustmentAmount || 20;
    const subscriptionDiscountMultiplier = priceAdjustmentType === 'PERCENTAGE' 
      ? (1 - (priceAdjustmentAmount / 100)) 
      : 1.0;
    
    // Calculate totals
    let subscriptionTotal = 0;
    let onetimeTotal = 0;
    
    this.bundleProducts.forEach(product => {
      const productId = product.product.id.replace('gid://shopify/Product/', '');
      const quantity = this.productQuantities[productId] || 0;
      
      if (quantity > 0) {
        const basePrice = product.product.price_min / 100;
        const volumeDiscountPercent = this.getVolumeDiscount(subQuantity);
        const volumeMult = 1 - (volumeDiscountPercent / 100);
        
        // Always use subscription discount multiplier for subscription price display
        subscriptionTotal += basePrice * subscriptionDiscountMultiplier * volumeMult * quantity;
        onetimeTotal += basePrice * volumeMult * quantity;
      }
    });
    
    // Calculate onetime addon total (always base price, no discounts)
    let onetimeAddonTotal = 0;
    const onetimeProds = this.onetimeProducts.length ? this.onetimeProducts : (window.onetimeBoxProducts || []);
    onetimeProds.forEach(product => {
      const productId = product.product.id.replace('gid://shopify/Product/', '');
      const quantity = this.onetimeProductQuantities[productId] || 0;
      if (quantity > 0) {
        const basePrice = product.product.price_min / 100;
        onetimeAddonTotal += basePrice * quantity;
      }
    });

    // Combined onetime total (box products at base price + onetime addon products)
    const combinedOnetimeTotal = onetimeTotal + onetimeAddonTotal;

    // Combined subscription total includes onetime addon at base price (no sub discount on those)
    const combinedSubscriptionTotal = subscriptionTotal + onetimeAddonTotal;

    // Calculate price per meal (convert to cents for formatPrice)
    const subscriptionPerMeal = totalMeals > 0 ? (combinedSubscriptionTotal / totalMeals) * 100 : 0;
    const onetimePerMeal = totalMeals > 0 ? (combinedOnetimeTotal / totalMeals) * 100 : 0;
    
    // Update displays
    const subscriptionPriceElement = document.querySelector('.subscription-price');
    const subscriptionTotalElement = document.querySelector('.subscription-box .total-meals');
    const onetimePriceElement = document.querySelector('.onetime-price');
    const onetimeTotalElement = document.querySelector('.onetime-box .total-meals');
    
    if (subscriptionPriceElement) {
      subscriptionPriceElement.textContent = this.formatPrice(subscriptionPerMeal);
    }
    if (subscriptionTotalElement) {
      subscriptionTotalElement.textContent = this.formatPrice(subscriptionTotal * 100);
    }
    if (onetimePriceElement) {
      onetimePriceElement.textContent = this.formatPrice(onetimePerMeal);
    }
    if (onetimeTotalElement) {
      onetimeTotalElement.textContent = this.formatPrice(combinedOnetimeTotal * 100);
    }

    // Show onetime addon breakdown only on the subscription box
    const subAddonEl = document.querySelector('.subscription-box .onetime-addon-total');
    if (subAddonEl) {
      if (onetimeAddonTotal > 0) {
        subAddonEl.textContent = `+ ${this.formatPrice(onetimeAddonTotal * 100)} (engångsköp)`;
        subAddonEl.style.display = '';
      } else {
        subAddonEl.style.display = 'none';
      }
    }
    
    // Update savings
    const savings = onetimeTotal - subscriptionTotal;
    const savingsElement = document.querySelector('.best-value-bar .savings-amount');
    if (savingsElement) {
      savingsElement.textContent = this.formatPrice((savings > 0 ? savings : 0) * 100);
    }
  }

  /**
   * Get total quantity of all selected products (subscription + onetime)
   */
  getTotalQuantity() {
    const subTotal = Object.values(this.productQuantities).reduce((sum, qty) => sum + qty, 0);
    const otTotal = Object.values(this.onetimeProductQuantities).reduce((sum, qty) => sum + qty, 0);
    return subTotal + otTotal;
  }

  /**
   * Get total quantity of subscription box products only (for volume discount)
   */
  getSubscriptionQuantity() {
    return Object.values(this.productQuantities).reduce((sum, qty) => sum + qty, 0);
  }

  /**
   * Get minimum quantity required from dynamicBoxData (same as volume discount)
   * Falls back to 2 if data not available
   */
  getMinimumQuantity() {
    if (window.dynamicBoxData?.sizeRange?.[0]) {
      return window.dynamicBoxData.sizeRange[0];
    }
    // Fallback to boxconfig if available
    if (this.box_config?.sizeRange?.[0]) {
      return this.box_config.sizeRange[0];
    }
    // Final fallback
    return 2;
  }

  /**
   * Update unit text displays throughout the UI
   */
  updateUnitTextDisplays() {
    // Update cart unit text
    document.querySelectorAll('.js-cart-unit-text, #cartUnitText').forEach(el => {
      el.textContent = this.unitText;
    });
    
    // Update per-meal static displays with singular form
    const singularUnit = this.unitText.replace(/er$/, ''); // Remove 'er' suffix for singular
    document.querySelectorAll('.dynamic-unit-text').forEach(el => {
      el.textContent = `/${singularUnit}`;
    });
  }

  /**
   * Update add to cart button
   */
  updateAddToCartButton() {
    const addToCartButtons = document.querySelectorAll('.js-add-to-cart, #addToCart');
    const totalQuantity = this.getTotalQuantity();
    
    // Calculate total price based on current selection
    const isSubscription = this.selectedOption === 'subscription';
    const sellingPlan = this.box_config.sellingPlanGroup?.[0];
    const priceAdjustmentAmount = sellingPlan?.priceAdjustmentAmount || 20;
    const subscriptionMultiplier = isSubscription ? (1 - (priceAdjustmentAmount / 100)) : 1.0;
    const volumeMultiplier = 1 - (this.getVolumeDiscount(totalQuantity) / 100);
    
    let totalPrice = 0;
    this.bundleProducts.forEach(product => {
      const productId = product.product.id.replace('gid://shopify/Product/', '');
      const quantity = this.productQuantities[productId] || 0;
      if (quantity > 0) {
        const basePrice = product.product.price_min / 100;
        totalPrice += basePrice * subscriptionMultiplier * volumeMultiplier * quantity;
      }
    });

    // Add onetime product prices (base price only, no discounts)
    const onetimeProds = this.onetimeProducts.length ? this.onetimeProducts : (window.onetimeBoxProducts || []);
    onetimeProds.forEach(product => {
      const productId = product.product.id.replace('gid://shopify/Product/', '');
      const quantity = this.onetimeProductQuantities[productId] || 0;
      if (quantity > 0) {
        const basePrice = product.product.price_min / 100;
        totalPrice += basePrice * quantity;
      }
    });
    
    // Calculate total meals dynamically (subscription + onetime)
    let totalMeals = 0;
    this.bundleProducts.forEach(product => {
      const productId = product.product.id.replace('gid://shopify/Product/', '');
      const quantity = this.productQuantities[productId] || 0;
      const antalPerProduct = product.antal_i || 14;
      totalMeals += quantity * antalPerProduct;
    });
    onetimeProds.forEach(product => {
      const productId = product.product.id.replace('gid://shopify/Product/', '');
      const quantity = this.onetimeProductQuantities[productId] || 0;
      const antalPerProduct = product.antal_i || 14;
      totalMeals += quantity * antalPerProduct;
    });
    
    // Update cart total meals display (all instances)
    document.querySelectorAll('.js-cart-total-meals, #cartTotalMeals').forEach(el => {
      const unitSpan = el.querySelector('.js-cart-unit-text, #cartUnitText');
      if (unitSpan) {
        el.childNodes[0].textContent = totalMeals + ' ';
      }
    });
    
    // Update button (min quantity check only considers subscription box products, not onetime addons)
    const minQuantity = this.getMinimumQuantity();
    const addBagsText = window.boxTranslations?.addBags || 'Lägg till {{ count }} påsar';
    addToCartButtons.forEach(button => {
      if (totalQuantity < minQuantity) {
        button.textContent = addBagsText.replace('{{ count }}', minQuantity);
        button.disabled = true;
      } else {
        const addText = window.boxTranslations?.add || 'Lägg till';
        button.textContent = `${addText} | ${this.formatPrice(totalPrice * 100)}`;
        button.disabled = false;
      }
    });
  }

  /**
   * Update volume discount progress bar based on total quantity
   */
  updateVolumeDiscountProgress() {
    if (!window.volumeDiscountProgressBar) return;
    
    const totalQuantity = this.getTotalQuantity();
    window.volumeDiscountProgressBar.updateProgress(totalQuantity);
  }

  /**
   * Handle add to cart
   */
  async handleAddToCart() {
    try {
      const selectedProducts = this.getSelectedProducts();
      const selectedOnetimeProducts = this.getSelectedOnetimeProducts();
      const minQuantity = this.getMinimumQuantity();
      const totalQuantity = this.getTotalQuantity();
      
      if ((selectedProducts.length + selectedOnetimeProducts.length) === 0 || totalQuantity < minQuantity) {
        const addBagsText = window.boxTranslations?.addBags || 'Lägg till {{ count }} påsar';
        alert(addBagsText.replace('{{ count }}', minQuantity));
        return;
      }
      
      let isSubscription = this.selectedOption === 'subscription';

      // Auto-switch to onetime if only onetime products are selected
      if (isSubscription && selectedProducts.length === 0) {
        this.selectOption('onetime');
        isSubscription = false;
      }
      
      if (isSubscription && !this.selectedSellingPlan) {
        const selectFrequencyText = window.boxTranslations?.selectDeliveryFrequency || 'Välj leveransfrekvens';
        alert(selectFrequencyText);
        return;
      }
      
      // Skio reads _dynamicBoxIndex as "which copy of this box"; a unique value per add split one customer into several boxes.
      const boxIndex = 0;
      const boxId = this.skioDynamicBox.box_id;
      
      // Build cart items for subscription/onetime box products
      const cartItems = selectedProducts.map(product => {
        const item = {
          id: product.variantId,
          quantity: product.quantity,
          properties: {
            _dynamicBoxIndex: boxIndex,
            _dynamicBoxId: boxId
          }
        };
        
        if (isSubscription && this.selectedSellingPlan) {
          item.selling_plan = this.selectedSellingPlan.gid.replace('gid://shopify/SellingPlan/', '');
        }
        
        return item;
      });

      // Add onetime-only products (never with selling_plan)
      selectedOnetimeProducts.forEach(product => {
        cartItems.push({
          id: product.variantId,
          quantity: product.quantity,
          properties: {
            _dynamicBoxIndex: boxIndex,
            _dynamicBoxId: boxId,
            _onetimeAddon: true
          }
        });
      });
      
      // Submit to cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: cartItems })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Failed to add items to cart');
      }
      
      // Reset all quantity selectors after successful add
      document.querySelectorAll('.product-item').forEach(item => {
        const input = item.querySelector('.quantity-input');
        const decreaseBtn = item.querySelector('.quantity-btn.decrease');
        if (input) {
          input.value = 0;
          input.classList.add('hidden');
        }
        if (decreaseBtn) {
          decreaseBtn.classList.add('hidden');
          decreaseBtn.disabled = true;
        }
      });
      Object.keys(this.productQuantities).forEach(id => { this.productQuantities[id] = 0; });
      Object.keys(this.onetimeProductQuantities).forEach(id => { this.onetimeProductQuantities[id] = 0; });
      this.selectOption('subscription');
      this.updateVolumeDiscountProgress();
      this.updateAddToCartButton();

      // Open cart drawer after adding items
      if (window.cart && typeof window.cart.getCart === 'function') {
        window.cart.getCart();
      } else {
        // Fallback: trigger cart drawer manually
        const drawerToggle = document.querySelector('[data-cart-drawer-toggle]');
        if (drawerToggle) {
          drawerToggle.click();
        }
      }
      
    } catch (error) {
      // console.error('❌ Error adding to cart:', error);
      alert(`Error: ${error.message}`);
    }
  }

  /**
   * Get selected products with quantities
   */
  getSelectedProducts() {
    const selected = [];
    
    Object.keys(this.productQuantities).forEach(productId => {
      const quantity = this.productQuantities[productId];
      if (quantity > 0) {
        const productData = this.bundleProducts.find(p => {
          const pId = p.product.id.replace('gid://shopify/Product/', '');
          return pId === productId;
        });
        
        if (productData) {
          selected.push({
            productId: productId,
            variantId: productData.variant_id,
            quantity: quantity
          });
        }
      }
    });
    
    return selected;
  }

  /**
   * Get selected onetime-only products with quantities
   */
  getSelectedOnetimeProducts() {
    const selected = [];
    const onetimeProds = this.onetimeProducts.length ? this.onetimeProducts : (window.onetimeBoxProducts || []);
    
    Object.keys(this.onetimeProductQuantities).forEach(productId => {
      const quantity = this.onetimeProductQuantities[productId];
      if (quantity > 0) {
        const productData = onetimeProds.find(p => {
          const pId = p.product.id.replace('gid://shopify/Product/', '');
          return pId === productId;
        });
        
        if (productData) {
          selected.push({
            productId: productId,
            variantId: productData.variant_id,
            quantity: quantity
          });
        }
      }
    });
    
    return selected;
  }

  /**
   * Get total quantity of onetime-only products
   */
  getOnetimeTotalQuantity() {
    return Object.values(this.onetimeProductQuantities).reduce((sum, qty) => sum + qty, 0);
  }

  /**
   * Setup intersection observer to show/hide fixed cart button
   */
  setupIntersectionObserver() {
    const fixedCartButton = document.querySelector('.add-to-cart-rendered');
    const desktopCartContainer = document.querySelector('.desktop-cart-container');
    
    if (fixedCartButton && desktopCartContainer) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Desktop cart is visible, hide the fixed button
            fixedCartButton.classList.add('hidden');
          } else {
            // Desktop cart is not visible, show the fixed button
            fixedCartButton.classList.remove('hidden');
          }
        });
      }, {
        threshold: 0.1 // Trigger when at least 10% of the element is visible
      });

      observer.observe(desktopCartContainer);
      // console.log('✅ Intersection observer setup for cart button');
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const subscriptionWrapper = document.querySelector('.subscription-wrapper');
  
  if (!subscriptionWrapper) {
    // console.log('DynamicBoxWidget: subscription-wrapper not found');
    return;
  }
  
  if (!window.skioDynamicBox || !window.bundleProducts) {
    // console.warn('DynamicBoxWidget: Required window objects not found');
    return;
  }
  
  // console.log('🚀 Initializing DynamicBoxWidget...');
  const widget = new DynamicBoxWidget();
  window.dynamicBoxWidget = widget;
});

// Handle page load and bfcache events
window.addEventListener('pageshow', function(event) {
  // Force reload when using back navigation
  if (event.persisted || performance.getEntriesByType("navigation")[0].type === "back_forward") {
    window.location.href = window.location.pathname + '?scroll=top';
  }
  
  // Check for scroll parameter and scroll to top
  if (window.location.search.includes('scroll=top')) {
    window.scrollTo(0, 0);
    // Remove the parameter from URL without reloading
    window.history.replaceState({}, '', window.location.pathname);
  }
});

// Add beforeunload handler to ensure clean state
window.addEventListener('beforeunload', function() {
  // Clear any stored state
  sessionStorage.clear();
});
